import React from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const About = () => {
  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const values = [
    {
      icon: FiIcons.FiUsers,
      title: 'Local Expertise',
      description: 'We live and work in the Hudson Valley, so we understand local businesses and provider options better than anyone.'
    },
    {
      icon: FiIcons.FiDollarSign,
      title: 'No Cost to You',
      description: 'Our service is completely free. We\'re compensated by providers, so you get all the benefits without any fees.'
    },
    {
      icon: FiIcons.FiShield,
      title: 'Trusted Partners',
      description: 'We\'ve built long-term relationships with all major internet providers in the region.'
    },
    {
      icon: FiIcons.FiTrendingUp,
      title: 'Proven Results',
      description: 'Hundreds of Hudson Valley businesses trust us to find better internet rates and service.'
    }
  ];

  const achievements = [
    { number: '500+', label: 'Businesses Served' },
    { number: '15+', label: 'Years Experience' },
    { number: '$2M+', label: 'Total Savings' },
    { number: '99%', label: 'Client Satisfaction' }
  ];

  return (
    <section id="about" className="py-20 bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl font-bold text-gray-900 mb-4">
            About{' '}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-cyan-600">
              Clark-Ritchotte
            </span>
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Your local internet experts helping Hudson Valley businesses save money and get better service since 2008
          </p>
        </motion.div>

        {/* Company Story */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.2 }}
          className="bg-white rounded-2xl shadow-xl p-8 mb-16"
        >
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Our Story</h3>
              <p className="text-gray-600 mb-4">
                Clark-Ritchotte Communications has been serving the Hudson Valley for over 15 years. We started with a simple mission: help local businesses get the internet service they need at prices they can afford.
              </p>
              <p className="text-gray-600 mb-4">
                As a division of RapidEdge, we leverage our extensive network and provider relationships to negotiate better rates than businesses could get on their own. Our deep understanding of the local market means we know which providers work best in different areas and buildings.
              </p>
              <p className="text-gray-600">
                We're not just brokers—we're your neighbors. We understand the challenges of running a business in the Hudson Valley and are committed to helping our community thrive.
              </p>
            </div>
            <div className="bg-gradient-to-br from-blue-50 to-cyan-50 rounded-xl p-8">
              <div className="grid grid-cols-2 gap-6">
                {achievements.map((achievement, index) => (
                  <div key={index} className="text-center">
                    <div className="text-3xl font-bold text-blue-600 mb-2">{achievement.number}</div>
                    <div className="text-gray-600 text-sm">{achievement.label}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </motion.div>

        {/* Values */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.4 }}
          className="mb-16"
        >
          <h3 className="text-2xl font-bold text-gray-900 mb-8 text-center">Why Businesses Trust Us</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => (
              <div key={index} className="bg-white rounded-xl p-6 text-center shadow-lg">
                <div className="flex items-center justify-center w-16 h-16 bg-blue-100 rounded-full mb-4 mx-auto">
                  <SafeIcon icon={value.icon} className="w-8 h-8 text-blue-600" />
                </div>
                <h4 className="text-xl font-bold text-gray-900 mb-2">{value.title}</h4>
                <p className="text-gray-600 text-sm">{value.description}</p>
              </div>
            ))}
          </div>
        </motion.div>

        {/* CTA Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.6 }}
          className="text-center"
        >
          <div className="bg-gradient-to-r from-blue-600 to-cyan-600 rounded-2xl p-12 text-white">
            <h3 className="text-3xl font-bold mb-4">
              Let's Help Your Business Thrive
            </h3>
            <p className="text-xl mb-8 opacity-90">
              Join hundreds of Hudson Valley businesses saving money with better internet service
            </p>
            <button
              onClick={() => scrollToSection('contact')}
              className="bg-white text-blue-600 px-8 py-4 rounded-lg text-lg font-semibold hover:bg-gray-100 transition-all duration-300 shadow-xl hover:shadow-2xl transform hover:-translate-y-1"
            >
              Get Free Consultation
            </button>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default About;